import React from 'react';
import { FileText, Loader2 } from 'lucide-react';

import { FileService } from '@/shared/storage/FileService';
import type { FileAttachment } from '@/shared/types/projectData';
import { cn } from '@/ui/theme/ThemeContext';
import { PdfCanvasPreview } from './PdfCanvasPreview';
import { SecureHtmlArtifactPreview } from './SecureHtmlArtifactPreview';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

interface AttachmentInlinePreviewProps {
  file: FileAttachment;
  className?: string;
  variant?: 'inline' | 'modal' | 'thumbnail';
}

const previewClassName = (variant: AttachmentInlinePreviewProps['variant']) => (
  variant === 'thumbnail'
    ? 'h-full min-h-0 border-0'
    : variant === 'modal'
    ? 'min-h-[360px]'
    : 'min-h-36'
);

const contentClassName = (variant: AttachmentInlinePreviewProps['variant']) => (
  variant === 'thumbnail' ? 'h-full' : variant === 'modal' ? 'h-[70vh]' : 'h-64'
);

const isDedicatedHtmlArtifact = (file: FileAttachment): boolean => (
  file.mimeType.split(';', 1)[0].trim().toLowerCase() === 'text/html'
  && /\.html?$/i.test(file.originalName.trim())
);

const GenericAttachmentInlinePreview: React.FC<AttachmentInlinePreviewProps> = ({
  file,
  className,
  variant = 'inline',
}) => {
  const { t } = useSettings();
  const [previewUrl, setPreviewUrl] = React.useState('');
  const [pdfData, setPdfData] = React.useState<Uint8Array | null>(null);
  const [isResolving, setIsResolving] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const { id, mimeType, originalName, thumbnailUrl, url } = file;

  React.useEffect(() => {
    let isMounted = true;
    let objectUrl = '';
    const abortController = new AbortController();
    const sourceUrl = url || thumbnailUrl || '';
    const isMediaPreview =
      FileService.isImageFile(mimeType) ||
      FileService.isVideoFile(mimeType) ||
      FileService.isAudioFile(mimeType);
    setPreviewUrl('');
    setPdfData(null);
    setHasError(false);

    const normalizedMime = mimeType.split(';', 1)[0].trim().toLowerCase();
    const canResolveDocument = FileService.isRenderedDocumentPreviewFile({ mimeType, originalName })
      || !normalizedMime || normalizedMime === 'application/octet-stream';
    if (!isMediaPreview && !canResolveDocument) {
      setIsResolving(false);
      return () => undefined;
    }

    if (!sourceUrl && isMediaPreview) {
      setIsResolving(false);
      return () => undefined;
    }

    const resolvePreviewUrl = async () => {
      try {
        setIsResolving(true);
        const previewSourceUrl = isMediaPreview
          ? sourceUrl
          : FileService.getRenderedPreviewSourceUrl({ id, mimeType, originalName, url });
        const absoluteUrl = FileService.toAbsoluteUrl(previewSourceUrl);
        if (!isMediaPreview) {
          const previewBlob = await FileService.fetchPreviewBlob(absoluteUrl, {
            signal: abortController.signal,
          });
          const expectsPdf = FileService.isPdfFile(mimeType, originalName)
            || FileService.isOfficeRenderedPreviewFile(mimeType, originalName);
          if (FileService.isPdfFile(previewBlob.type, expectsPdf ? 'preview.pdf' : '')) {
            const previewData = new Uint8Array(await previewBlob.arrayBuffer());
            if (isMounted) {
              setPdfData(previewData);
            }
            return;
          }
          if (expectsPdf) {
            throw new Error(`Document preview returned ${previewBlob.type || 'an unknown content type'} instead of application/pdf`);
          }

          objectUrl = URL.createObjectURL(previewBlob);
          if (isMounted) {
            setPreviewUrl(objectUrl);
          } else {
            URL.revokeObjectURL(objectUrl);
          }
          return;
        }
        if (FileService.isProtectedApiFileUrl(absoluteUrl)) {
          objectUrl = await FileService.createAuthenticatedObjectUrl(absoluteUrl);
          if (isMounted) {
            setPreviewUrl(objectUrl);
          } else {
            URL.revokeObjectURL(objectUrl);
          }
          return;
        }
        if (isMounted) {
          setPreviewUrl(absoluteUrl);
        }
      } catch (error) {
        if (isMounted && !(error instanceof DOMException && error.name === 'AbortError')) {
          setHasError(true);
        }
      } finally {
        if (isMounted) {
          setIsResolving(false);
        }
      }
    };

    void resolvePreviewUrl();

    return () => {
      isMounted = false;
      abortController.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [id, mimeType, originalName, thumbnailUrl, url]);

  const shellClassName = cn(
    'relative overflow-hidden rounded-lg border border-black/10 bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.04]',
    previewClassName(variant),
    className,
  );

  if (isResolving) {
    return (
      <div
        className={cn(shellClassName, 'flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-notion-text-secondary')}
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>{t('Loading preview…')}</span>
      </div>
    );
  }

  if (hasError || (!previewUrl && !pdfData)) {
    return (
      <div
        className={cn(shellClassName, 'flex flex-col items-center justify-center gap-2 p-4 text-center text-sm text-gray-500 dark:text-notion-text-secondary')}
        role={hasError ? 'alert' : undefined}
      >
        <FileText className="h-5 w-5 opacity-60" aria-hidden="true" />
        <span>{t('Preview unavailable')}</span>
        <span>{t('Download remains available from the file controls.')}</span>
      </div>
    );
  }

  if (FileService.isImageFile(file.mimeType)) {
    return (
      <div className={shellClassName} data-testid="attachment-inline-image-preview">
        <img
          src={previewUrl}
          alt={file.originalName}
          className={cn(contentClassName(variant), 'max-h-full w-full', variant === 'thumbnail' ? 'object-cover' : 'object-contain')}
          onError={() => setHasError(true)}
        />
      </div>
    );
  }

  if (FileService.isVideoFile(file.mimeType)) {
    return (
      <div className={shellClassName} data-testid="attachment-inline-video-preview">
        <video
          src={previewUrl}
          controls
          preload="metadata"
          className={cn(contentClassName(variant), 'w-full object-contain')}
          onError={() => setHasError(true)}
        />
      </div>
    );
  }

  if (FileService.isAudioFile(file.mimeType)) {
    return (
      <div className={cn(shellClassName, 'flex items-center p-4')} data-testid="attachment-inline-audio-preview">
        <audio
          src={previewUrl}
          controls
          preload="metadata"
          className="w-full"
          onError={() => setHasError(true)}
        />
      </div>
    );
  }

  if (pdfData) {
    return (
      <div className={cn(shellClassName, contentClassName(variant), 'bg-white dark:bg-notion-bg-secondary')} data-testid="attachment-rendered-document-preview">
        <PdfCanvasPreview
          data={pdfData}
          fileName={file.originalName}
          variant={variant}
        />
      </div>
    );
  }

  if (previewUrl) {
    return (
      <div className={cn(shellClassName, 'bg-white dark:bg-notion-bg-secondary')} data-testid="attachment-rendered-document-preview">
        <iframe
          src={previewUrl}
          sandbox=""
          title={t('Preview of {{fileName}}', { fileName: file.originalName })}
          className={cn(contentClassName(variant), 'block w-full border-0 bg-white')}
        />
      </div>
    );
  }

  return null;
};

export const AttachmentInlinePreview: React.FC<AttachmentInlinePreviewProps> = (props) => {
  if (isDedicatedHtmlArtifact(props.file)) {
    return <SecureHtmlArtifactPreview {...props} />;
  }
  const { id, checksum, mimeType, originalName, thumbnailUrl, url } = props.file;
  return <GenericAttachmentInlinePreview key={JSON.stringify([id, checksum, mimeType, originalName, thumbnailUrl, url])} {...props} />;
};
